import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/** Passphrase required in admin UI to confirm destructive deletes (UX gate only; not a security boundary). */
export const ADMIN_DELETE_CONFIRM_PASSCODE = 'CartCo@123';

export interface ConfirmDialogTextInput {
  label: string;
  placeholder?: string;
  minLength?: number;
}

export interface ConfirmDialogData {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /**
   * Optional third action (shown between Cancel and Confirm).
   * Emits `{ confirmed: true, choice: 'alt' }` when pressed.
   */
  altConfirmText?: string;
  /** When set, the user must enter this exact value before Confirm is enabled. */
  confirmationPasscode?: string;
  /** When set, shows a text area; the trimmed value is returned on confirm. */
  textInput?: ConfirmDialogTextInput;
}

export type ConfirmDialogCloseResult =
  | { confirmed: false }
  | { confirmed: true; value?: string; choice?: 'confirm' | 'alt' };

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <!-- z above cart checkout-bar (z-[200]) and other app chrome so overlay covers full screen -->
    <div
      class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      (click)="onCancel()"
    >
      <div
        class="mx-4 flex w-full max-w-md max-h-[min(90vh,40rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
        (click)="$event.stopPropagation()"
      >
        <div class="flex flex-shrink-0 items-start justify-between gap-3 px-6 pb-3 pt-6">
          <h3 class="mb-0 flex-1 text-lg font-semibold text-slate-900 dark:text-white">
            {{ data.title || 'Confirm' }}
          </h3>
          <button
            type="button"
            class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 active:scale-95 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            (click)="onCancel()"
            aria-label="Close"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6">
          <p class="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
            {{ data.message }}
          </p>
          <div *ngIf="data.confirmationPasscode" class="mt-4 pb-2">
            <label class="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Passcode to confirm
            </label>
            <input
              type="password"
              [(ngModel)]="passcodeInput"
              autocomplete="off"
              class="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-cartco-green focus:ring-2 focus:ring-cartco-green/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              placeholder="Enter passcode"
            />
          </div>
          <div *ngIf="data.textInput" class="mt-4 pb-2">
            <label class="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {{ data.textInput.label }}
            </label>
            <textarea
              [(ngModel)]="textInputValue"
              rows="3"
              [attr.placeholder]="data.textInput.placeholder || ''"
              class="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-cartco-green focus:ring-2 focus:ring-cartco-green/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            ></textarea>
            <p
              *ngIf="data.textInput.minLength"
              class="mt-1.5 text-xs text-slate-400 dark:text-slate-500"
            >
              Minimum {{ data.textInput.minLength }} characters
            </p>
          </div>
        </div>
        <div
          class="flex flex-shrink-0 flex-col-reverse gap-2 px-6 pb-6 pt-4 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3"
        >
          <button
            *ngIf="data.cancelText"
            type="button"
            (click)="onCancel()"
            class="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            {{ data.cancelText }}
          </button>
          <button
            *ngIf="data.altConfirmText"
            type="button"
            (click)="onAltConfirm()"
            [disabled]="confirmDisabled"
            class="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            {{ data.altConfirmText }}
          </button>
          <button
            type="button"
            (click)="onConfirm()"
            [disabled]="confirmDisabled"
            class="rounded-xl bg-cartco-green px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#26a855] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {{ data.confirmText || 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class ConfirmDialogComponent {
  data: ConfirmDialogData = { message: '' };
  @Output() closed = new EventEmitter<ConfirmDialogCloseResult>();
  passcodeInput = '';
  textInputValue = '';

  get confirmDisabled(): boolean {
    if (this.data.confirmationPasscode && this.passcodeInput !== this.data.confirmationPasscode) {
      return true;
    }
    if (this.data.textInput) {
      const minLength = this.data.textInput.minLength ?? 1;
      return this.textInputValue.trim().length < minLength;
    }
    return false;
  }

  onConfirm(): void {
    if (this.confirmDisabled) {
      return;
    }
    this.closed.emit({
      confirmed: true,
      choice: 'confirm',
      value: this.data.textInput ? this.textInputValue.trim() : undefined,
    });
  }

  onAltConfirm(): void {
    if (this.confirmDisabled) {
      return;
    }
    this.closed.emit({
      confirmed: true,
      choice: 'alt',
      value: this.data.textInput ? this.textInputValue.trim() : undefined,
    });
  }

  onCancel(): void {
    this.closed.emit({ confirmed: false });
  }
}

